import React, { createContext, useContext } from 'react';
import NotFound from 'src/pages/Status/NotFound';
import { Flex, Text } from '@chakra-ui/react';
import { Button } from '@morressier/alembic';
import { useKeycloak } from 'src/components/AuthHOC';

import { FEATURE_FLAGS } from 'src/constants';
import { useFlag } from '@unleash/proxy-client-react';
import { useAuthRealm } from '../routes/AuthRealmAccessControl/AuthRealmContext';
import { SignUpForm } from 'src/pages/Sign-Up/SignUpForm';
import { basicBrandLayout, twoColumnBrandLayout } from 'src/pages/Branding';
import { twoColumnBrandLayoutGeneric } from 'src/pages/Branding/twoColumnBrandLayoutGeneric';

export type IBrandLayoutProps = {
  children: React.ReactNode;
};

type IBrandConfig = {
  authContent: {
    Title: JSX.Element;
    Description: JSX.Element;
    Provider: JSX.Element;
  };
  BrandLayout: (props: IBrandLayoutProps) => JSX.Element;
};

export const BrandConfigContext = createContext<IBrandConfig | undefined>(
  undefined
);

export const useBrandConfig = (): IBrandConfig => {
  const context = useContext(BrandConfigContext);
  if (!context) {
    throw new Error('useBrandData must be used within a BrandDataProvider');
  }

  return context;
};

const BrandConfigProvider = ({ children }: IBrandLayoutProps) => {
  // 1. Get current auth realm
  const { authRealm, getLoginUrlForAuthRealm, loginUrl, clientBrand } =
    useAuthRealm();
  const { logout, keycloak } = useKeycloak();
  const enableKeycloakOnIOPP = useFlag(FEATURE_FLAGS.ioppKeycloak);

  if (!clientBrand) return <NotFound />;

  const handleClick = (loginUrl: string) => {
    if (keycloak?.authenticated) {
      // If there is a logged in user, log them out first and then have them redirected
      // to the desired loginUrl via KeyCloak's post-logout redirection.
      return logout(loginUrl);
    }

    // If there is NO logged in user, send them directly to the desired loginUrl.
    window.location.href = loginUrl;
  };

  const BasicAuth = {
    Title: <Text textStyle="headingTitle">Sign In</Text>,
    Description: (
      <Text>
        {' '}
        Lorem ipsum dolor sit amet, consectetur adipiscing elit. Etiam et leo in
        tellus elementum sollicitudin. Duis placerat dignissim congue. Ut nunc
        elit, volutpat a dignissim in, auctor commodo arcu. Praesent
        sollicitudin, neque vel mattis interdum, nibh felis ultrices neque.
      </Text>
    ),
    Provider: (
      <Flex gap={2}>
        {/* TODO: Change render to each auth realm's providers */}
        <Button
          variant={'primary'}
          onClick={() => handleClick(getLoginUrlForAuthRealm(authRealm))}
        >
          Continue with KeyCloak
        </Button>
      </Flex>
    )
  };

  const TwoColumnAuth = {
    Title: <Text textStyle="headingTitle">Sign In</Text>,
    Description: (
      <Text>
        {' '}
        Lorem ipsum dolor sit amet, consectetur adipiscing elit. Etiam et leo in
        tellus elementum sollicitudin. Duis placerat dignissim congue. Ut nunc
        elit, volutpat a dignissim in, auctor commodo arcu. Praesent
        sollicitudin, neque vel mattis interdum, nibh felis ultrices neque.
      </Text>
    ),
    Provider: (
      <Flex gap={2}>
        {/* TODO: Change render to each auth realm's providers */}
        <Button onClick={() => handleClick(loginUrl)}>
          Continue with {authRealm.toUpperCase()}
        </Button>
        {authRealm === 'iopp' && enableKeycloakOnIOPP ? (
          // Technically IOPP doesn't use KeyCloak, but we need to log in somehow with our test users (E.Rodger/stancy@cs.ox.ac.uk, etc.)
          // which only exist on KeyCloak and are manually mapped to IOPP. This is a convenience feature to ease testing.
          <Button
            variant={'secondary'}
            onClick={() => handleClick(getLoginUrlForAuthRealm('m'))}
          >
            Continue with KeyCloak
          </Button>
        ) : null}
      </Flex>
    )
  };

  const TwoColumnAuthGeneric = {
    Title: <></>,
    Description: <></>,
    Provider: <SignUpForm />
  };

  const layoutTemplates: { [key: string]: IBrandConfig } = {
    twoColumnAuth: {
      authContent: TwoColumnAuth,
      BrandLayout: twoColumnBrandLayout(clientBrand)
    },
    twoColumnAuthGeneric: {
      authContent: TwoColumnAuthGeneric,
      BrandLayout: twoColumnBrandLayoutGeneric(clientBrand)
    },
    basicAuth: {
      authContent: BasicAuth,
      BrandLayout: basicBrandLayout(clientBrand)
    }
  };

  const config = layoutTemplates[clientBrand.template];
  if (!config) return <NotFound />;

  return (
    <BrandConfigContext.Provider value={config}>
      {children}
    </BrandConfigContext.Provider>
  );
};

export default BrandConfigProvider;
